import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import config from 'app-config';
import Spinner from '@chronos/components/Spinner';
import CollapseIcon from '@chronos/components/Icons/CollapseIcon';
import Button from '@chronos/components/Button';
import { useGetAuthUserQuery } from '@chronos/slices/usersApi';
import {
  initialBoardsState,
  useGetBoardsQuery,
} from '@chronos/slices/boardsApi';
import trelloApi from '@chronos/api-client/trelloApi';
import { tryAuthorize } from './utils/initialize';
import { getPWADisplayMode, hashToObject } from './utils';
import AuthScreen from './components/AuthScreen';
import {
  mapState,
  setDisplayMode,
  setProfile,
  setShowInstallPrompt,
  setTrelloToken,
} from './slices/appSlice';
import * as S from './styled';

const desktopMinWidth = 500;

const App = (props = {}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const state = useSelector(mapState);

  const { isLoaded, showInstallPrompt } = state;

  const { token: hashToken, logout: hashLogout } = hashToObject(
    location.hash.substring(1)
  );
  const trelloToken = window.localStorage.getItem('trello_token') || hashToken;
  const isAuthorized = typeof trelloToken === 'string';

  const [authUrl, setAuthUrl] = useState(!trelloToken);

  const boardId = useParams().boardId || null;
  const navigate = useNavigate();
  const isBrowser = window.matchMedia('(display-mode: browser)').matches;

  useEffect(() => {
    if (trelloToken) {
      window.localStorage.setItem('trello_token', trelloToken);
      dispatch(setTrelloToken(trelloToken));
      trelloApi.setKeyAndToken(config.trelloApiKey, trelloToken);
    }
  }, [trelloToken]);

  const { data: userData, isSuccess: isUserLoaded } = useGetAuthUserQuery(
    null,
    { skip: !isAuthorized }
  );

  const {
    data: { ids: boardsIds, entities: boardsEntities } = initialBoardsState,
    isSuccess: isBoardsLoaded,
  } = useGetBoardsQuery(
    { memberId: userData?.id || null },
    { skip: !isAuthorized || !isUserLoaded }
  );
  const currentBoard = boardsEntities[boardId];

  const isAppLoaded = isBoardsLoaded && isUserLoaded;

  useEffect(() => {
    if (!hashLogout) {
      return;
    }
    window.localStorage.removeItem('trello_token');
    setAuthUrl(tryAuthorize());
    navigate('/');
  }, [hashLogout]);

  useEffect(() => {
    if (userData) {
      dispatch(setProfile(userData));

      if (config.useSentry) {
        Sentry.configureScope((scope) => {
          scope.setUser({
            id: userData.id,
            username: userData.username,
            fullName: userData.fullName,
          });
        });
      }
    }
  }, [userData]);

  useEffect(() => {
    if (!isAuthorized) {
      setAuthUrl(tryAuthorize());
    } else {
      if (isLoaded) {
        return;
      }
      window.localStorage.setItem('trello_token', trelloToken);

      if (hashToken) {
        navigate('/');
      }
    }
  }, []);

  const displayMode = getPWADisplayMode();
  useEffect(() => {
    dispatch(setDisplayMode(displayMode));
    if (displayMode === 'browser') {
      const installPromptHide = localStorage.getItem('installPromptHide', null);
      if (!installPromptHide) {
        dispatch(setShowInstallPrompt(true));
      }
    }
  }, [displayMode]);

  useEffect(() => {
    if (!isBoardsLoaded || boardId) return;

    const changeBoard = (id) => {
      navigate(`/${id}`);
    };

    const id = window.localStorage.getItem('boardId');
    if (id) {
      // redirect to last used boardId
      changeBoard(id);
    } else if (boardsIds.length) {
      // redirect to first board
      changeBoard(boardsIds[0]);
    }
  }, [isBoardsLoaded, boardId, boardsIds]);

  useEffect(() => {
    if (!boardId) return;

    window.localStorage.setItem('boardId', boardId);
  }, [boardId]);

  useEffect(() => {
    const isExpanded = !!Number(
      window.localStorage.getItem('isLabelExpanded', 0)
    );

    if (isExpanded !== state.isLabelExpanded) {
      window.localStorage.setItem(
        'isLabelExpanded',
        state.isLabelExpanded ? 1 : 0
      );
    }
  }, [state.isLabelExpanded]);

  const onAuthorize = useCallback(() => {
    window.location.href = authUrl;
  }, [authUrl]);

  if (authUrl) {
    return (
      <S.App>
        <S.ScreenCenter>
          <AuthScreen
            onClick={onAuthorize}
            showInstallPrompt={showInstallPrompt}
          />
        </S.ScreenCenter>
      </S.App>
    );
  }

  if (!isAppLoaded) {
    return (
      <Spinner
        size="xlarge"
        style={{ height: '100vh' }}
      />
    );
  }

  const outletElement = <Outlet />;
  return (
    <S.App
      background={{
        backgroundImages: currentBoard?.prefs?.backgroundImageScaled,
        backgroundColor: currentBoard?.prefs?.backgroundColor,
      }}
    >
      <S.ScreenCenter>{props.children || outletElement}</S.ScreenCenter>
      {!isBrowser && (
        <>
          <S.FixedBotomLeft>
            <Button
              withIcon
              spacing="none"
              onClick={() => {
                window.resizeTo(
                  desktopMinWidth,
                  window.screen?.availHeight ?? window.screen.height
                );
                setTimeout(() => {
                  window.moveTo(0, 0);
                }, 100);
              }}
            >
              <CollapseIcon
                size="large"
                shouldPadding
              />
            </Button>
          </S.FixedBotomLeft>
          <S.FixedBotomRight>
            <Button
              withIcon
              spacing="none"
              onClick={() => {
                window.resizeTo(
                  desktopMinWidth,
                  window.screen?.availHeight ?? window.screen.height
                );
                setTimeout(() => {
                  window.moveTo(
                    window.screen?.availWidth ?? window.screen.width,
                    0
                  );
                }, 100);
              }}
            >
              <CollapseIcon
                size="large"
                shouldPadding
              />
            </Button>
          </S.FixedBotomRight>
        </>
      )}
    </S.App>
  );
};

App.propTypes = {
  children: PropTypes.node,
};

export default App;
